import { useMemo, useState } from "react";

import {
    AddressCopy,
    Column,
    Icon,
    OutlinedCard,
    PopoverWrapper,
    Row,
    StatColumn,
    StatProps,
    Text,
    TextButton,
    TextVariant,
    usePopover
} from "@bridgesplit/ui";
import { BsMetaUtil, getRaydiumPositionYield, getZcLedger, useClaimableRaydiumFee } from "@bridgesplit/abf-react";
import {
    TokenImage,
    Slippage,
    TokenAmountWithUsd,
    PriceRangeStatus,
    YieldToggle,
    OutstandingDebt
} from "app/components/common";
import { Collapse, Divider } from "@mui/material";
import { ExpandLess, ExpandMore, SwapHorizOutlined } from "@mui/icons-material";
import { formatPercent, formatUsd } from "@bridgesplit/utils";
import { ClaimRaydiumFees } from "app/components/common/raydium";

import { useRaydiumContext } from "./RaydiumContext";
import { useRaydiumFormatPrice } from "./util";
import { TickWithPercentChange } from "./common";

export default function RaydiumInfo() {
    return (
        <>
            <Header />
            <Column spacing={1}>
                <RaydiumFlipPrice />
                <ManageRaydium />
            </Column>
            <PricesStats />
        </>
    );
}

function Header() {
    const { raydiumPosition, slippageController, raydiumOffchain } = useRaydiumContext();
    return (
        <Row spaceBetween spacing={2}>
            <Row spacing={1}>
                <TokenImage size="md" metadata={raydiumPosition.poolMetadata} />
                <Text variant="h4">
                    {BsMetaUtil.getSymbol(raydiumPosition.tokenA.metadata)}-
                    {BsMetaUtil.getSymbol(raydiumPosition.tokenB.metadata)}
                </Text>
                <Text color="caption">
                    {formatPercent(raydiumOffchain?.feeRate ?? 0, { minimumSignificantDigits: 1 })}
                </Text>
                <Addresses />
            </Row>
            <Row spacing={1}>
                <Slippage.Button {...slippageController.slippageButtonProps} />
            </Row>
        </Row>
    );
}

function useRaydiumYieldData() {
    const { raydiumPosition, poolData, raydiumOffchain } = useRaydiumContext();
    const positionUsdValue = raydiumPosition.totalPrice;

    return useMemo(() => {
        if (!raydiumPosition || !poolData || !raydiumOffchain) return undefined;
        return getRaydiumPositionYield({
            positionLiquidity: raydiumPosition.position.liquidity,
            poolLiquidity: raydiumPosition.pool.liquidity,
            raydiumOffchain,
            positionUsdValue
        });
    }, [poolData, positionUsdValue, raydiumOffchain, raydiumPosition]);
}

function Addresses() {
    const { open, popoverProps } = usePopover();
    const { raydiumPosition } = useRaydiumContext();

    const variant: TextVariant = "body2";
    const positionStats: StatProps[] = [
        {
            caption: "Position address",
            value: <AddressCopy variant={variant} address={raydiumPosition.position.nftMint} />
        }
    ];

    const poolStats: StatProps[] = [
        {
            caption: "Pool address",
            value: <AddressCopy variant={variant} address={raydiumPosition.poolAddress} />
        },
        {
            caption: BsMetaUtil.getSymbol(raydiumPosition.tokenA.metadata),
            value: <AddressCopy variant={variant} address={raydiumPosition.tokenA.mint} />
        },
        {
            caption: BsMetaUtil.getSymbol(raydiumPosition.tokenB.metadata),
            value: <AddressCopy variant={variant} address={raydiumPosition.tokenB.mint} />
        }
    ];

    return (
        <>
            <TextButton color="caption" onClick={open}>
                <Icon type="tooltip" />
            </TextButton>
            <PopoverWrapper {...popoverProps}>
                <Column sx={{ minWidth: "250px" }} spacing={1} p={2}>
                    <StatColumn variant={variant} captionVariant={variant} stats={positionStats} />
                    <Divider />
                    <StatColumn variant={variant} captionVariant={variant} stats={poolStats} />
                </Column>
            </PopoverWrapper>
        </>
    );
}

function RaydiumFlipPrice() {
    const {
        isFlippedPrice,
        setIsFlippedPrice,
        raydiumPosition: { tokenA, tokenB }
    } = useRaydiumContext();

    const tokens = isFlippedPrice ? [tokenB, tokenA] : [tokenA, tokenB];

    return (
        <Row spaceBetween spacing={1}>
            <Text variant="h4">Your position</Text>
            <TextButton color="caption" onClick={() => setIsFlippedPrice((prev) => !prev)}>
                {BsMetaUtil.getSymbol(tokens[0].metadata)}/{BsMetaUtil.getSymbol(tokens[1].metadata)}
                <SwapHorizOutlined />
            </TextButton>
        </Row>
    );
}

function ManageRaydium() {
    const context = useRaydiumContext();
    const {
        raydiumPosition,
        loanExpanded,
        poolData,
        positionData,
        isActionTypeInitialized: initializedActionTypes,
        reset
    } = context;

    const isDepositActionInitialized = initializedActionTypes?.includes("deposit");

    const yieldData = useRaydiumYieldData();
    const ledger = getZcLedger(loanExpanded);
    const { data: claimableFees, isLoading } = useClaimableRaydiumFee(raydiumPosition, poolData, positionData);

    const stats: StatProps[] = [];
    if (yieldData) {
        stats.push({
            caption: "Estimated yield",
            value: <YieldToggle variant={"body1"} yieldData={yieldData} />
        });
    }

    if (!isLoading && claimableFees !== undefined && claimableFees.feesUsd !== 0) {
        stats.push({
            caption: "Pending yield",
            value: (
                <ClaimRaydiumFees
                    raydiumPosition={raydiumPosition}
                    claimableFees={claimableFees}
                    loanExpanded={loanExpanded}
                    isActionTypeInitialized={isDepositActionInitialized}
                    reset={reset}
                />
            )
        });
    }

    if (loanExpanded.principalMetadata && ledger) {
        stats.push({
            caption: "Outstanding debt",
            value: <OutstandingDebt loanExpanded={loanExpanded} />
        });
    }

    return (
        <>
            <StatColumn stats={stats} />
            <Divider />
            <DepositAmounts />
        </>
    );
}

function DepositAmounts() {
    const { raydiumPosition } = useRaydiumContext();
    const [expanded, setExpanded] = useState(false);

    return (
        <Column>
            <Row spaceBetween>
                <Text color="caption">Deposits</Text>
                <Row sx={{ cursor: "pointer" }} onClick={() => setExpanded((prev) => !prev)}>
                    <Text>{formatUsd(raydiumPosition.totalPrice)}</Text>
                    <TextButton color="disabled">{expanded ? <ExpandLess /> : <ExpandMore />}</TextButton>
                </Row>
            </Row>
            <Collapse in={expanded}>
                <Column mt={1}>
                    <StatColumn
                        stats={[raydiumPosition.tokenA, raydiumPosition.tokenB].map((t) => ({
                            caption: BsMetaUtil.getSymbol(t.metadata),
                            value: <TokenAmountWithUsd amount={t.amount} token={t.metadata} amountUsd={t.usdAmount} />
                        }))}
                    />
                </Column>
            </Collapse>
        </Column>
    );
}

function PricesStats() {
    const { raydiumPosition, poolData, isFlippedPrice } = useRaydiumContext();
    const formatPrice = useRaydiumFormatPrice();

    const variant: TextVariant = "body2";
    const stats: StatProps[] = [
        {
            caption: isFlippedPrice ? "Max price" : "Min price",
            value: (
                <TickWithPercentChange
                    variant={variant}
                    tick={raydiumPosition.position.tickLowerIndex}
                    tickLower={raydiumPosition.position.tickLowerIndex}
                    tickUpper={raydiumPosition.position.tickUpperIndex}
                />
            )
        },
        {
            caption: "Current price",
            value: (
                <>
                    <Text loadingWidth="40px" loading={!poolData} variant={variant}>
                        {formatPrice(raydiumPosition.pool.tickCurrent, 6)}
                    </Text>
                    <PriceRangeStatus
                        variant="caption"
                        tickLower={raydiumPosition.position.tickLowerIndex}
                        tickUpper={raydiumPosition.position.tickUpperIndex}
                        tickCurrentIndex={raydiumPosition.pool.tickCurrent}
                    />
                </>
            )
        },
        {
            caption: isFlippedPrice ? "Min price" : "Max price",
            value: (
                <TickWithPercentChange
                    variant={variant}
                    tick={raydiumPosition.position.tickUpperIndex}
                    tickLower={raydiumPosition.position.tickLowerIndex}
                    tickUpper={raydiumPosition.position.tickUpperIndex}
                />
            )
        }
    ];
    if (isFlippedPrice) {
        stats.reverse();
    }

    return (
        <OutlinedCard>
            <Row sx={{ alignItems: "flex-start" }}>
                {stats.map((s, i) => (
                    <Column
                        flexGrow={1}
                        key={i}
                        spacing={0.5}
                        alignItems={(() => {
                            if (i === 0) return "flex-start";
                            if (i === 2) return "flex-end";
                            return "center";
                        })()}
                    >
                        <Text color="caption" variant="caption">
                            {s.caption}
                        </Text>
                        {s.value}
                    </Column>
                ))}
            </Row>
        </OutlinedCard>
    );
}
